import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { orderAPI } from '../services/api';
import OrderCard from './OrderCard';

const SearchBar = () => {
  const { selectedShop } = useOutletContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Debounce ref
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear search if shop changes to prevent data leakage
    setQuery('');
    setResults([]);
    setHasSearched(false);
  }, [selectedShop]);

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      // ✅ EXACT MAPPING: GET /orders/search?q={query}&shop={shop}
      const response = await orderAPI.search(searchTerm, selectedShop);
      setResults(response.data || []);
      setHasSearched(true);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    // Debounce: Wait 300ms after user stops typing to hit the API
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      performSearch(val);
    }, 300);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };

  return (
    <div className="relative w-full z-30 mb-6">
      {/* Search Input Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={`Search orders in ${selectedShop}... (ID, Name, Phone, Issue)`}
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm shadow-sm transition-colors"
        />
        {query && (
          <button 
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Results Overlay — completely covers the screen content below it */}
      {(query || hasSearched) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-50 min-h-[50vh] rounded-xl shadow-2xl border border-gray-200 p-4 z-40 max-h-[75vh] overflow-y-auto">
          
          <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
            <h3 className="font-bold text-gray-700">Search Results</h3>
            {isSearching && <span className="text-sm text-blue-600 font-medium animate-pulse">Searching...</span>}
          </div>

          {!isSearching && hasSearched && results.length === 0 ? (
            <div className="text-center py-10 text-gray-500 font-medium">
              No orders found matching "{query}"
            </div>
          ) : (
            <div className="space-y-3">
              {results.map(order => (
                <OrderCard 
                  key={order.order_id} 
                  order={order} 
                  onUpdate={() => performSearch(query)} 
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;